import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTrip } from '../../contexts/TripContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { NotificationBell } from '../common/NotificationBell';
import { ThemeToggle } from '../common/ThemeToggle';
import { LanguageToggle } from '../common/LanguageToggle';
import { TutorialOverlay } from '../common/TutorialOverlay';
import { PWAInstallButton } from '../common/PWAInstallPrompt';
import { VersionBadge } from '../common/AppFooter';
import { 
  Home, Map, LogOut, Menu, HelpCircle, MapPin, Settings
} from 'lucide-react';
import { Button } from '../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../ui/tooltip';
import { parseISO, isBefore, format } from 'date-fns';
import { ptBR, enUS } from 'date-fns/locale';

export const Sidebar = ({ tripId, isOpen, onClose }) => {
  const { user, logout } = useAuth();
  const tripContext = useTrip();
  const trips = tripContext?.trips || [];
  const fetchTrips = tripContext?.fetchTrips;
  const location = useLocation();
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const dateLocale = language === 'en-US' ? enUS : ptBR;

  // Fetch trips if not loaded yet (happens when user refreshes on a trip page)
  useEffect(() => {
    if (fetchTrips && trips.length === 0) {
      fetchTrips().catch(err => console.log('Error fetching trips for sidebar:', err));
    }
  }, [fetchTrips, trips.length]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  // Navigation items with translations
  const navItems = [
    { path: '/dashboard', icon: Home, labelKey: 'sidebar.dashboard' },
    { path: '/trips', icon: Map, labelKey: 'sidebar.trips' },
  ];

  // Calcular próximas 3 viagens ordenadas por data
  const upcomingTrips = Array.isArray(trips) ? trips
    .filter(t => {
      if (!t?.start_date) return false;
      try {
        const startDate = typeof t.start_date === 'string' ? parseISO(t.start_date) : t.start_date;
        return isBefore(new Date(), startDate) && t.status !== 'cancelled';
      } catch {
        return false;
      }
    })
    .sort((a, b) => {
      const dateA = typeof a.start_date === 'string' ? parseISO(a.start_date) : a.start_date;
      const dateB = typeof b.start_date === 'string' ? parseISO(b.start_date) : b.start_date;
      return dateA - dateB;
    })
    .slice(0, 3) : [];

  return (
    <>
      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={onClose}
        />
      )}
      
      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 h-full w-64 bg-white dark:bg-slate-900 border-r border-slate-100 dark:border-slate-800 z-50
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-slate-100 dark:border-slate-800">
            <Link to="/dashboard" className="flex items-center gap-3" onClick={onClose}>
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-orange-400 to-orange-600 flex items-center justify-center">
                <span className="text-white text-xl">✈️</span>
              </div>
              <span className="font-heading font-bold text-xl text-slate-900 dark:text-white">ViajeJunto</span>
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 px-4 py-3 rounded-xl transition-colors
                  ${isActive(item.path) 
                    ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' 
                    : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'}
                `}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{t(item.labelKey)}</span>
              </Link>
            ))}

            {/* Próximas Viagens - Sempre mostrar (máximo 3) */}
            {upcomingTrips.length > 0 && (
              <>
                <div className="pt-4 pb-2">
                  <p className="px-4 text-xs font-semibold text-slate-400 dark:text-slate-500 uppercase tracking-wider">
                    {t('sidebar.upcomingTrips')}
                  </p>
                </div>
                {upcomingTrips.map((trip) => {
                  const startDate = typeof trip.start_date === 'string' ? parseISO(trip.start_date) : trip.start_date;
                  const formattedDate = format(startDate, "dd MMM", { locale: dateLocale });
                  
                  return (
                    <Link
                      key={trip.id}
                      to={`/trip/${trip.id}`}
                      onClick={onClose}
                      className={`
                        flex items-start gap-3 px-4 py-3 rounded-xl transition-colors
                        ${location.pathname.startsWith(`/trip/${trip.id}`)
                          ? 'bg-orange-50 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400' 
                          : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'}
                      `}
                    >
                      <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm leading-tight line-clamp-2">{trip.name}</p>
                        <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">{formattedDate}</p>
                      </div>
                    </Link>
                  );
                })}
              </>
            )}
          </nav>

          {/* Install App Button */}
          <div className="px-4 py-2">
            <PWAInstallButton variant="sidebar" />
          </div>

          {/* User Section */}
          <div className="p-4 border-t border-slate-100 dark:border-slate-800">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                  <Avatar className="w-10 h-10 flex-shrink-0">
                    {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user?.name} />}
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400 text-lg">
                      {user?.avatar_emoji || '😊'}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0 text-left">
                    <p className="font-medium text-slate-900 dark:text-white truncate">{user?.name}</p>
                    <p className="text-sm text-slate-500 dark:text-slate-400 truncate max-w-[160px]">{user?.email}</p>
                  </div>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                side="right" 
                align="end" 
                className="w-56"
                sideOffset={8}
              >
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  {t('common.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </aside>
    </>
  );
};

export const TopBar = ({ onMenuClick, title }) => {
  const { user, logout, api } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [showTutorial, setShowTutorial] = React.useState(false);

  const handleStartTutorial = async () => {
    try {
      await api.post('/tutorial/restart');
      setShowTutorial(true);
    } catch (error) {
      console.log('Error restarting tutorial:', error);
      // Show tutorial anyway
      setShowTutorial(true);
    }
  };

  return (
    <>
      <header className="sticky top-0 z-30 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md border-b border-slate-100 dark:border-slate-800">
        <div className="flex items-center justify-between px-4 py-3 lg:px-6">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={onMenuClick}
              data-testid="menu-toggle"
            >
              <Menu className="w-5 h-5" />
            </Button>
            {title && (
              <h1 className="font-heading font-bold text-xl text-slate-900 dark:text-white">{title}</h1>
            )}
          </div>

          <div className="flex items-center gap-2">
            {/* Tutorial - Hidden on mobile, shown on desktop */}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleStartTutorial}
                    className="hidden sm:flex text-slate-500 hover:text-orange-500"
                    data-testid="tutorial-btn"
                  >
                    <HelpCircle className="w-5 h-5" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('common.viewTutorial')}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
            {/* Language & Theme - Hidden on mobile, shown on desktop */}
            <div className="hidden sm:flex items-center gap-2">
              <LanguageToggle />
              <ThemeToggle />
            </div>
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu">
                  <Avatar className="w-8 h-8">
                    {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user?.name} />}
                    <AvatarFallback className="bg-orange-100 dark:bg-orange-900/50 text-orange-600 dark:text-orange-400">
                      {user?.avatar_emoji || '😊'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:inline font-medium dark:text-white">{user?.name}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent 
                align="end" 
                className="w-56"
                sideOffset={8}
                collisionPadding={16}
              >
                <DropdownMenuItem onClick={handleStartTutorial}>
                  <HelpCircle className="w-4 h-4 mr-2" />
                  {t('common.viewTutorial')}
                </DropdownMenuItem>
                {/* Mobile only - Language & Theme inside menu */}
                <div className="sm:hidden">
                  <DropdownMenuSeparator />
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.language')}</p>
                    <LanguageToggle variant="menu" />
                  </div>
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">{t('common.theme')}</p>
                    <ThemeToggle variant="menu" />
                  </div>
                </div>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/settings')}>
                  <Settings className="w-4 h-4 mr-2" />
                  {t('common.settings')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => { logout(); navigate('/'); }} className="text-red-600">
                  <LogOut className="w-4 h-4 mr-2" />
                  {t('common.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>

      {/* Tutorial Overlay */}
      {showTutorial && (
        <TutorialOverlay 
          onClose={() => setShowTutorial(false)} 
          onComplete={() => setShowTutorial(false)} 
        />
      )}
    </>
  );
};

export const DashboardLayout = ({ children, tripId, title }) => {
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  return (
    <div className="min-h-screen-safe bg-slate-50 dark:bg-slate-950 transition-colors safe-area flex flex-col">
      <Sidebar tripId={tripId} isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      
      <div className="lg:ml-64 flex-1 flex flex-col">
        <TopBar onMenuClick={() => setSidebarOpen(true)} title={title} />
        <main className="p-4 lg:p-6 safe-area-bottom flex-1">
          {children}
        </main>
        
        {/* App Version Footer */}
        <footer className="py-3 px-4 text-center border-t border-slate-200 dark:border-slate-800">
          <p className="text-[10px] text-slate-400 dark:text-slate-500 flex items-center justify-center gap-2">
            <span>ViajeJunto</span>
            <VersionBadge />
          </p>
        </footer>
      </div>
    </div>
  );
};

export default DashboardLayout;
